import { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import FormulationFilter from '@/components/products/components/FormulationFilter/FormulationFilter';
import ProductTile from '@/components/products/components/ProductTile/ProductTile';
import useMedicineInformation from '@/hooks/medicine-information/useMedicineInformation';

import { PatientConsentContext } from '@/context/PatientConsentProvider';
import useCustomer from '@/hooks/user/useCustomer';
import CTASection from '@/pages/medicine-information/components/CTASection';
import { PRODUCT_DETAIL_PATH } from '@/routes/route-paths';
import { Alert, AlertTitle, Box } from '@montugroup/design-system';
import { Grid2 } from '@mui/material';
import HeadingSection from './components/HeadingSection';
import PageError from './components/PageError';
import PageSkeleton from './components/PageSkeleton';

export default function MedicineInformation() {
  const { data: customer } = useCustomer();
  const isDischarged = customer?.isDischarged;

  const {
    query: { isLoading, isError },
    filteredProducts,
    getProductCardProps,
    getFormulationFilterProps,
  } = useMedicineInformation();

  const { requiresConsentSubmission } = useContext(PatientConsentContext);
  const showOrderButton = !isDischarged && !requiresConsentSubmission;

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError) {
    return <PageError />;
  }

  const hasAnyProducts = filteredProducts.length > 0;

  return (
    <Box maxWidth="xl">
      <HeadingSection />
      {showOrderButton && <CTASection />}
      <Box marginBottom={4}>
        <FormulationFilter {...getFormulationFilterProps()} />
      </Box>
      {hasAnyProducts ? (
        <Grid2 container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid2 size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={product.id}>
              <RouterLink to={`${PRODUCT_DETAIL_PATH}${product.id}`} style={{ textDecoration: 'none' }}>
                <ProductTile {...getProductCardProps(product)} />
              </RouterLink>
            </Grid2>
          ))}
        </Grid2>
      ) : (
        <Alert severity="info" sx={{ maxWidth: 600 }}>
          <AlertTitle>No Products</AlertTitle>
          Please check your filter options, or your prescriptions page.
        </Alert>
      )}
    </Box>
  );
}
